import React, { useEffect, useState } from "react";
import { Icon, Spinner, SpinnerSize } from "@fluentui/react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { accessService } from "../../services/accessService";

// ─────────────────────────────────────────────────────────────────────────────
// EntityDrawer — slide-out panel that renders the Entity 360 profile for the
// entity the user picked from search results. Generic shape driven by the
// backend response, so adding new entity types only requires a server-side
// branch and a catalog row.
// ─────────────────────────────────────────────────────────────────────────────

type ColumnFormat = "currency" | "percent" | "number" | undefined;
type Align = "left" | "right" | undefined;

interface Column {
  key: string;
  label: string;
  align?: Align;
  format?: ColumnFormat;
}
interface Section {
  title: string;
  type: "table";
  empty?: string;
  columns: Column[];
  rows: any[];
}
interface Kpi {
  label: string;
  value: string;
  raw: number;
  hint?: string;
}
interface TrendPoint {
  periodNum: number;
  periodName: string;
  actual?: number;
  budget?: number;
}
interface EntityProfile {
  entityType: string;
  id: string;
  header: { title: string; subtitle?: string; badges?: string[] };
  kpis: Kpi[];
  monthlyTrend: TrendPoint[] | null;
  sections: Section[];
}

interface Props {
  entityType: string;
  id: string;
  fallbackTitle?: string;
  onClose: () => void;
}

// ── Formatting helpers ────────────────────────────────────────────────────
function fmtCurrency(v: any): string {
  const n = Number(v);
  if (!isFinite(n)) return "—";
  if (Math.abs(n) >= 1_000_000) return "$" + (n / 1_000_000).toFixed(2) + "M";
  if (Math.abs(n) >= 1_000)     return "$" + (n / 1_000).toFixed(0) + "k";
  return "$" + Math.round(n).toLocaleString("en-AU");
}
function fmtAxis(v: number): string {
  if (Math.abs(v) >= 1_000_000) return (v / 1_000_000).toFixed(1) + "M";
  if (Math.abs(v) >= 1_000) return (v / 1_000).toFixed(0) + "k";
  return String(Math.round(v));
}
function fmtCell(val: any, col: Column): string {
  if (val == null) return "—";
  if (col.format === "currency") return fmtCurrency(val);
  if (col.format === "percent") {
    const n = Number(val);
    return isFinite(n) ? n.toFixed(1) + "%" : "—";
  }
  if (col.format === "number") {
    const n = Number(val);
    return isFinite(n) ? n.toLocaleString("en-AU") : String(val);
  }
  return String(val);
}

// ── Component ────────────────────────────────────────────────────────────
export default function EntityDrawer({ entityType, id, fallbackTitle, onClose }: Props) {
  const [data, setData] = useState<EntityProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    setData(null);
    (async () => {
      try {
        const res = await accessService.get<EntityProfile>(
          `/search/entity/${encodeURIComponent(entityType)}/${encodeURIComponent(id)}`
        );
        if (alive) setData(res.data);
      } catch (e: any) {
        if (alive) setError(e?.response?.data?.error || e.message || "Failed to load");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [entityType, id]);

  return (
    <>
      <style>{`
        .ed-backdrop {
          position: fixed; inset: 0;
          background: rgba(15, 23, 19, 0.42);
          backdrop-filter: blur(2px);
          z-index: 1100;
          animation: ed-fade 0.16s ease;
        }
        @keyframes ed-fade { from { opacity: 0; } to { opacity: 1; } }
        .ed-drawer {
          position: fixed;
          top: 0; right: 0; bottom: 0;
          width: min(880px, 65vw);
          background: #FFFFFF;
          box-shadow: -16px 0 48px rgba(0,0,0,0.18);
          z-index: 1101;
          display: grid;
          grid-template-rows: auto 1fr;
          animation: ed-slide 0.24s cubic-bezier(.16,.84,.36,1);
        }
        @keyframes ed-slide { from { transform: translateX(24px); opacity: 0; } to { transform: none; opacity: 1; } }
        @media (max-width: 720px) {
          .ed-drawer { width: 100vw; }
        }

        /* Header band */
        .ed-head {
          padding: 22px 28px 20px;
          border-bottom: 1px solid #EDEDED;
          background: linear-gradient(to bottom, #FFFFFF, #FAFAFA);
        }
        .ed-head-top {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 12px;
          font-size: 11px;
          color: #747474;
        }
        .ed-head-top .ed-crumb {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          background: none;
          border: none;
          color: #2e873d;
          font-size: 11px;
          font-weight: 600;
          font-family: inherit;
          cursor: pointer;
          padding: 0;
        }
        .ed-head-top .ed-crumb:hover { text-decoration: underline; }
        .ed-close {
          margin-left: auto;
          background: none;
          border: 1px solid #E1E1E1;
          border-radius: 4px;
          width: 28px;
          height: 28px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          color: #484848;
        }
        .ed-close:hover { background: #F5F5F5; color: #232323; border-color: #C4C9D0; }
        .ed-eyebrow {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: #747474;
          margin-bottom: 6px;
        }
        .ed-title {
          font-size: 22px;
          font-weight: 800;
          letter-spacing: -0.02em;
          color: #232323;
          line-height: 1.2;
        }
        .ed-subtitle {
          font-size: 13px;
          color: #747474;
          margin-top: 6px;
          line-height: 1.5;
        }
        .ed-badges { display: flex; gap: 6px; margin-top: 12px; flex-wrap: wrap; }
        .ed-badge {
          display: inline-block;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #2e873d;
          background: #d9f4d7;
          padding: 3px 8px;
          border-radius: 3px;
        }

        /* Body */
        .ed-body {
          overflow-y: auto;
          padding: 24px 28px 32px;
        }
        .ed-body::-webkit-scrollbar { width: 6px; }
        .ed-body::-webkit-scrollbar-thumb { background: #DADCDF; border-radius: 3px; }

        .ed-kpis {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
          gap: 12px;
          margin-bottom: 28px;
        }
        .ed-kpi {
          padding: 14px 16px;
          background: #FFFFFF;
          border: 1px solid #E1E1E1;
          border-radius: 6px;
        }
        .ed-kpi-label {
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #747474;
          margin-bottom: 6px;
        }
        .ed-kpi-value {
          font-size: 22px;
          font-weight: 700;
          color: #232323;
          letter-spacing: -0.02em;
          line-height: 1.2;
          font-variant-numeric: tabular-nums;
        }
        .ed-kpi-hint { font-size: 11px; color: #747474; margin-top: 4px; }

        .ed-section { margin-bottom: 28px; }
        .ed-section-title {
          font-size: 13px;
          font-weight: 700;
          color: #232323;
          margin-bottom: 12px;
          letter-spacing: -0.005em;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .ed-chart {
          background: #FFFFFF;
          border: 1px solid #EDEDED;
          border-radius: 6px;
          padding: 16px 12px 12px;
        }
        .ed-table-wrap {
          background: #FFFFFF;
          border: 1px solid #EDEDED;
          border-radius: 6px;
          overflow: hidden;
        }
        .ed-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12.5px;
        }
        .ed-table th {
          background: #FAFAFA;
          padding: 8px 12px;
          text-align: left;
          font-weight: 600;
          font-size: 11px;
          color: #484848;
          border-bottom: 1px solid #E1E1E1;
          white-space: nowrap;
          letter-spacing: 0.02em;
          text-transform: uppercase;
        }
        .ed-table th.right { text-align: right; }
        .ed-table td {
          padding: 8px 12px;
          border-bottom: 1px solid #F0F0F0;
          color: #232323;
          vertical-align: top;
          font-variant-numeric: tabular-nums;
        }
        .ed-table td.right { text-align: right; }
        .ed-table tr:last-child td { border-bottom: none; }
        .ed-table tr:hover td { background: #FAFAFA; }
        .ed-table-empty {
          padding: 24px;
          text-align: center;
          color: #9aa0a6;
          font-size: 12.5px;
        }

        .ed-loading, .ed-error {
          padding: 80px 24px;
          text-align: center;
          color: #747474;
          font-size: 13px;
        }
        .ed-error { color: #B91C1C; }
      `}</style>

      <div className="ed-backdrop" onClick={onClose} aria-hidden="true" />
      <aside className="ed-drawer" role="dialog" aria-modal="true" aria-label="Entity profile">
        <div className="ed-head">
          <div className="ed-head-top">
            <button className="ed-crumb" onClick={onClose}>
              <Icon iconName="Back" style={{ fontSize: 10 }} /> Back to search
            </button>
            <button className="ed-close" onClick={onClose} aria-label="Close">
              <Icon iconName="Cancel" style={{ fontSize: 12 }} />
            </button>
          </div>
          <div className="ed-eyebrow">{labelForType(entityType)}</div>
          <div className="ed-title">{data?.header.title || fallbackTitle || "Loading…"}</div>
          {(data?.header.subtitle) && (
            <div className="ed-subtitle">{data.header.subtitle}</div>
          )}
          {(data?.header.badges && data.header.badges.length > 0) && (
            <div className="ed-badges">
              {data.header.badges.map((b) => (
                <span key={b} className="ed-badge">{b}</span>
              ))}
            </div>
          )}
        </div>

        <div className="ed-body">
          {loading ? (
            <div className="ed-loading">
              <Spinner size={SpinnerSize.large} label="Loading profile…" />
            </div>
          ) : error ? (
            <div className="ed-error">{error}</div>
          ) : data ? (
            <>
              {data.kpis && data.kpis.length > 0 && (
                <div className="ed-kpis">
                  {data.kpis.map((k, i) => (
                    <div key={i} className="ed-kpi">
                      <div className="ed-kpi-label">{k.label}</div>
                      <div className="ed-kpi-value">{k.value}</div>
                      {k.hint ? <div className="ed-kpi-hint">{k.hint}</div> : null}
                    </div>
                  ))}
                </div>
              )}

              {data.monthlyTrend && data.monthlyTrend.length > 0 && (
                <div className="ed-section">
                  <div className="ed-section-title">
                    <Icon iconName="LineChart" style={{ fontSize: 13, color: "#2e873d" }} />
                    Monthly trend (FY26)
                  </div>
                  <div className="ed-chart">
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={data.monthlyTrend} margin={{ top: 8, right: 18, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#EDEDED" />
                        <XAxis dataKey="periodName" tick={{ fontSize: 10, fill: "#484848" }} />
                        <YAxis tickFormatter={fmtAxis} tick={{ fontSize: 10, fill: "#747474" }} />
                        <Tooltip
                          contentStyle={{ backgroundColor: "#FFFFFF", border: "1px solid #E1E1E1", borderRadius: 4, fontSize: 12 }}
                          formatter={(v: number) => fmtCurrency(v)}
                        />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        {hasField(data.monthlyTrend, "actual") && (
                          <Line type="monotone" dataKey="actual" name="Actuals" stroke="#2e873d" strokeWidth={2} dot={{ r: 3 }} />
                        )}
                        {hasField(data.monthlyTrend, "budget") && (
                          <Line type="monotone" dataKey="budget" name="Budget"  stroke="#9CA3AF" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 3 }} />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {data.sections.map((sec, i) => (
                <div key={i} className="ed-section">
                  <div className="ed-section-title">
                    <Icon iconName="Table" style={{ fontSize: 13, color: "#2e873d" }} />
                    {sec.title}
                  </div>
                  <div className="ed-table-wrap">
                    {sec.rows && sec.rows.length > 0 ? (
                      <table className="ed-table">
                        <thead>
                          <tr>
                            {sec.columns.map((c) => (
                              <th key={c.key} className={c.align === "right" ? "right" : ""}>
                                {c.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {sec.rows.map((row: any, ri: number) => (
                            <tr key={ri}>
                              {sec.columns.map((c) => (
                                <td key={c.key} className={c.align === "right" ? "right" : ""}>
                                  {fmtCell(row[c.key], c)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="ed-table-empty">{sec.empty || "No data"}</div>
                    )}
                  </div>
                </div>
              ))}
            </>
          ) : null}
        </div>
      </aside>
    </>
  );
}

function labelForType(type: string): string {
  switch (type) {
    case "cost_centre":     return "Cost Centre";
    case "project":         return "Project";
    case "supplier":        return "Supplier";
    case "purchase_order":  return "Purchase Order";
    case "property":        return "Property";
    case "asset":           return "Asset";
    case "service_request": return "Service Request";
    case "permit":          return "Permit";
    case "infringement":    return "Infringement";
    case "customer":        return "Customer";
    default: return type;
  }
}

function hasField(arr: any[], key: string): boolean {
  return arr.some((r) => r && r[key] != null && r[key] !== 0);
}
